import { createContext, useCallback, useContext, useEffect, useState, type AnchorHTMLAttributes, type ReactNode } from 'react';
import { APP_CONFIG } from './config';

/**
 * Minimal router: history API in production (Netlify rewrites to index.html),
 * hash mode for static previews. Paths are locale-agnostic for now; a /en or /pl
 * prefix can be parsed here when those locales go live.
 */

export const ROUTES = {
  home: '/',
  candleConfigurator: '/configurator/candle',
} as const;

const hashMode = APP_CONFIG.routerMode === 'hash';

function readPath(): { path: string; anchor: string } {
  if (hashMode) {
    const h = window.location.hash.replace(/^#/, '');
    if (h.startsWith('/')) {
      const [p, a = ''] = h.split('#');
      return { path: p || '/', anchor: a };
    }
    return { path: '/', anchor: h };
  }
  return { path: window.location.pathname || '/', anchor: window.location.hash.replace(/^#/, '') };
}

interface RouterValue {
  path: string;
  anchor: string;
  navigate: (to: string) => void;
}

const RouterContext = createContext<RouterValue | null>(null);

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState(readPath);

  useEffect(() => {
    const onChange = () => setLoc(readPath());
    window.addEventListener(hashMode ? 'hashchange' : 'popstate', onChange);
    return () => window.removeEventListener(hashMode ? 'hashchange' : 'popstate', onChange);
  }, []);

  const navigate = useCallback((to: string) => {
    const [p, a = ''] = to.split('#');
    const path = p || '/';
    if (hashMode) {
      window.location.hash = a ? `${path}#${a}` : path;
    } else {
      window.history.pushState(null, '', a ? `${path}#${a}` : path);
      setLoc({ path, anchor: a });
    }
    if (a) {
      requestAnimationFrame(() => document.getElementById(a)?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
    } else {
      window.scrollTo({ top: 0 });
    }
  }, []);

  return <RouterContext.Provider value={{ path: loc.path, anchor: loc.anchor, navigate }}>{children}</RouterContext.Provider>;
}

export function useRouter(): RouterValue {
  const v = useContext(RouterContext);
  if (!v) throw new Error('useRouter outside RouterProvider');
  return v;
}

export function hrefFor(to: string): string {
  if (!hashMode) return to;
  return `#${to}`;
}

/** In-app link. Section anchors on the home page use "/#section". */
export function Link({ to, onClick, ...rest }: AnchorHTMLAttributes<HTMLAnchorElement> & { to: string }) {
  const { navigate } = useRouter();
  return (
    <a
      href={hrefFor(to)}
      onClick={(e) => {
        onClick?.(e);
        if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
        e.preventDefault();
        navigate(to);
      }}
      {...rest}
    />
  );
}
